import { DragOverlay, defaultDropAnimationSideEffects } from "@dnd-kit/core";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { Button, Icon } from "semantic-ui-react";

import { useCan } from "components";
import { useDeleteServicePendingChecklistMutation } from "modules/PendingChecklistsDashboard/components/ServiceChecklists/components/ServiceChecklistModal/components/SortableItem/hooks";
import { useChecklistDropdownOptions } from "modules/PendingChecklistsDashboard/hooks";

import type { UniqueIdentifier } from "@dnd-kit/core";
import type { Checklist } from "models";
import type { SortableServicePendingChecklist } from "modules/PendingChecklistsDashboard/components/ServiceChecklists/components";
import type { CSSProperties } from "react";

type SortableItemProps = {
  item: SortableServicePendingChecklist;
  checklists?: Checklist[];
};

type DragHandleProps = {
  id: UniqueIdentifier;
};

export const SortableOverlay = () => <DragOverlay dropAnimation={{ sideEffects: defaultDropAnimationSideEffects({ styles: { active: { opacity: "0.4" } } }) }} />;

const DragHandle = ({ id }: DragHandleProps) => {
  const canReorderPinPendingChecklist = useCan("update", "check-in-service-pending-checklist");
  const { attributes, listeners, setActivatorNodeRef } = useSortable({ id });

  return (
    <button {...attributes} {...listeners} ref={setActivatorNodeRef} className="drag-button" disabled={!canReorderPinPendingChecklist}>
      <Icon className="move" />
    </button>
  );
};

export const SortableItem = ({ item, checklists }: SortableItemProps) => {
  const canDeletePendingChecklist = useCan("delete", "check-in-service-pending-checklist");
  const { isDragging, setNodeRef, transform, transition } = useSortable({ id: item.id });
  const { mutate: deleteServicePendingChecklist } = useDeleteServicePendingChecklistMutation();
  const { groupedChecklistsDropdownOptions } = useChecklistDropdownOptions(checklists);

  const style: CSSProperties = { opacity: isDragging ? 0.4 : 1, transform: CSS.Translate.toString(transform), transition };
  const infoText = groupedChecklistsDropdownOptions.find(checklist => checklist.value === item.checklist_id)?.infoText;

  return (
    <li ref={setNodeRef} style={style}>
      <div className="input-wrapper">
        <div className="ordering-number">{item.ordering}</div>
        <span>
          {item.checklist_name} {infoText && <p> - {infoText} </p>}
        </span>
        <DragHandle id={item.id} />
      </div>

      <Button basic onClick={() => deleteServicePendingChecklist(item)} className="delete-button" icon disabled={!canDeletePendingChecklist}>
        <Icon className="trash red" />
      </Button>
    </li>
  );
};
